import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';

const AffordabilityCalculatorLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Instant Affordability Analysis',
      description: 'See exactly how much property you can afford based on your finances',
      icon: '💰',
    },
    {
      title: 'Income Assessment',
      description: 'Factor in your salary, savings, and down payment capacity',
      icon: '💼',
    },
    {
      title: 'Expense Tracking',
      description: 'Consider monthly expenses to ensure realistic affordability',
      icon: '📋',
    },
    {
      title: 'Multiple Scenarios',
      description: 'Test different income, expense, and loan scenarios',
      icon: '📊',
    },
    {
      title: 'Debt Consideration',
      description: 'Factor in existing debts and their impact on borrowing capacity',
      icon: '💳',
    },
    {
      title: 'Irish Standards',
      description: 'Uses Irish banking standards and lending requirements',
      icon: '🏦',
    },
  ];

  const benefits = [
    'Know your realistic budget before house hunting',
    'Understand how much you can truly borrow',
    'Plan your savings goals accurately',
    'Make informed financial decisions',
    'Avoid overextending yourself financially',
  ];

  return (
    <>
      <Helmet>
        <title>Affordability Calculator Ireland | How Much Can I Afford? | RentSmart.ie</title>
        <meta
          name="description"
          content="Use RentSmart's free Affordability Calculator to determine how much property you can realistically afford. Based on Irish banking standards for accurate results."
        />
        <meta
          name="keywords"
          content="affordability calculator, how much can I afford, mortgage affordability, property budget, Ireland, first-time buyers, home buying budget"
        />
        <link
          rel="canonical"
          href="https://rentsmart.ie/property-tools/affordability-calculator-landing"
        />
        <meta property="og:title" content="Affordability Calculator - RentSmart.ie" />
        <meta
          property="og:description"
          content="Calculate how much property you can afford with RentSmart's free Affordability Calculator."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://rentsmart.ie/property-tools/affordability-calculator-landing"
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              How Much Property Can
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">
                You Actually Afford?
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Get a realistic assessment of your property budget based on your income, expenses, and
              financial situation. Perfect for first-time buyers and investors.
            </p>
            <button
              onClick={() => navigate('/property-tools/affordability-calculator')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Check My Affordability
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Comprehensive Financial Analysis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Check Affordability First?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircleIcon className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-amber-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              What Gets Calculated
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-amber-600 mb-4">💵 Income Analysis</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Gross annual income</span>
                  </li>
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Available down payment</span>
                  </li>
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Savings and assets</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-amber-600 mb-4">💸 Expense Analysis</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Monthly living expenses</span>
                  </li>
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Existing debts and loans</span>
                  </li>
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Obligations and commitments</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-amber-600 mb-4">📊 Lending Capacity</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Maximum loan amount</span>
                  </li>
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Debt-to-income ratio</span>
                  </li>
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Borrowing recommendations</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-amber-600 mb-4">🏠 Property Budget</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Maximum property price</span>
                  </li>
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Recommended budget range</span>
                  </li>
                  <li className="flex gap-2">
                    <span>✓</span>
                    <span>Safe borrowing limits</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: 1,
                  title: 'Enter Income',
                  description: 'Provide your annual income and available savings',
                },
                {
                  step: 2,
                  title: 'List Expenses',
                  description: 'Share your monthly expenses and existing debts',
                },
                {
                  step: 3,
                  title: 'Get Results',
                  description: 'See your affordable property budget instantly',
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-600 to-orange-600">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Start Your Property Search with Confidence
            </h2>
            <p className="text-lg mb-8 text-amber-100">
              Know your budget before you start house hunting
            </p>
            <button
              onClick={() => navigate('/property-tools/affordability-calculator')}
              className="inline-flex items-center gap-2 bg-white text-amber-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Use Affordability Calculator
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default AffordabilityCalculatorLandingPage;
